/**
 * CalendarView — widok kalendarza miesięcznego dla rekordów modułu custom (F12.E).
 *
 * Rozwiązuje rekordy modułu (`ModuleRecord` + `ModuleField[]`) do wspólnego kształtu
 * `CalendarEvent` i renderuje współdzielony `MonthGrid` (ten sam co w silniku list —
 * `CalendarBoard`). Zachowuje: auto-detekcję pierwszego pola daty lub wybór ręczny
 * (gdy >1), kolorowanie po polu statusu (`options[].color`) + legendę, `onDayClick`
 * (np. pre-fill create) oraz pasek „Brak daty".
 *
 * Bez zewnętrznych bibliotek (pure Date API — helpery w `calendar/calendar-utils`).
 */

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type CalendarEvent,
  DEFAULT_ENTRY_COLOR,
  extractDateStr,
  STATUS_FALLBACK_COLOR,
} from '@/components/calendar/calendar-utils';
import { MonthGrid } from '@/components/calendar/MonthGrid';
import { Dropdown } from '@/components/Dropdown';
import type { ModuleField } from '@/components/fields/FieldRegistry';
import { Spinner } from '@/components/Spinner';

import type { ModuleRecord } from './record-types';

interface Props {
  records: ModuleRecord[];
  fields: ModuleField[];
  /** Slug pola daty startu. Jeśli null/undefined → auto-detect pierwszego pola daty. */
  dateFieldSlug?: string | null;
  /**
   * Slug pola daty końca (opcjonalny). Gdy ustawiony i pole istnieje, wpis pokrywa
   * każdy dzień zakresu [start, end] zamiast pojedynczego dnia startu.
   */
  endDateFieldSlug?: string | null;
  /**
   * Slug pola statusu (select, opcjonalny). Gdy ustawiony, wpisy są kolorowane
   * kolorem opcji (`field.options[].color`) + rysowana jest legenda.
   */
  statusFieldSlug?: string | null;
  isLoading?: boolean;
  onRecordClick: (record: ModuleRecord) => void;
  onDayClick?: (date: Date) => void;
}

export function CalendarView({
  records,
  fields,
  dateFieldSlug: dateFieldSlugProp,
  endDateFieldSlug,
  statusFieldSlug,
  isLoading,
  onRecordClick,
  onDayClick,
}: Props) {
  const { t } = useTranslation();

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  // Pola date/datetime dostępne jako pole startu (wybór ręczny gdy jest więcej niż jedno).
  const dateFields = useMemo(
    () => fields.filter((f) => f.fieldType === 'date' || f.fieldType === 'datetime'),
    [fields],
  );

  // Ręczne nadpisanie pola startu przez Dropdown (null = brak → z propsa/configu).
  // DERYWUJEMY efektywny slug zamiast trzymać go w stanie z inicjalizacją, żeby
  // `dateFieldSlug` z configu (ładowany async) i pola ładowane później nie zostały
  // zignorowane przy pierwszym mouncie.
  const [manualStartSlug, setManualStartSlug] = useState<string | null>(null);
  const selectedFieldSlug =
    manualStartSlug ??
    (dateFieldSlugProp !== undefined ? dateFieldSlugProp : (dateFields[0]?.slug ?? null));

  const activeDateField = dateFields.find((f) => f.slug === selectedFieldSlug) ?? null;
  const activeEndField = useMemo(
    () => (endDateFieldSlug ? (dateFields.find((f) => f.slug === endDateFieldSlug) ?? null) : null),
    [dateFields, endDateFieldSlug],
  );
  const activeStatusField = useMemo(
    () => (statusFieldSlug ? (fields.find((f) => f.slug === statusFieldSlug) ?? null) : null),
    [fields, statusFieldSlug],
  );

  const rowsById = useMemo(() => {
    const map = new Map<string, ModuleRecord>();
    for (const rec of records) map.set(rec.id, rec);
    return map;
  }, [records]);

  const { events, noDateEvents } = useMemo(() => {
    const withDate: CalendarEvent[] = [];
    const without: CalendarEvent[] = [];

    const colorFor = (rec: ModuleRecord): string => {
      if (!activeStatusField) return DEFAULT_ENTRY_COLOR;
      const val = (rec as Record<string, unknown>)[activeStatusField.slug];
      const opt = activeStatusField.options?.find((o) => o.value === val);
      return opt?.color ?? STATUS_FALLBACK_COLOR;
    };

    for (const rec of records) {
      const id = rec.id;
      const label = rec.record_name ?? rec.id.slice(0, 8);
      const color = colorFor(rec);

      const startKey = activeDateField
        ? extractDateStr((rec as Record<string, unknown>)[activeDateField.slug])
        : null;
      if (!startKey) {
        without.push({ id, label, color, startKey: '', endKey: '' });
        continue;
      }
      const rawEnd = activeEndField
        ? extractDateStr((rec as Record<string, unknown>)[activeEndField.slug])
        : null;
      const endKey = rawEnd && rawEnd >= startKey ? rawEnd : startKey;
      withDate.push({ id, label, color, startKey, endKey });
    }
    return { events: withDate, noDateEvents: without };
  }, [records, activeDateField, activeEndField, activeStatusField]);

  const handleEventClick = useCallback(
    (eventId: string) => {
      const rec = rowsById.get(eventId);
      if (rec) onRecordClick(rec);
    },
    [rowsById, onRecordClick],
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (dateFields.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-text-secondary">{t('tenant.modules.calendar.noDateFields')}</p>
        <p className="mt-1 text-xs text-text-muted">
          {t('tenant.modules.calendar.noDateFieldsHint')}
        </p>
      </div>
    );
  }

  const legend =
    activeStatusField && (activeStatusField.options?.length ?? 0) > 0
      ? (activeStatusField.options ?? []).map((o) => ({
          label: o.label,
          color: o.color ?? STATUS_FALLBACK_COLOR,
        }))
      : undefined;

  const toolbarExtra =
    dateFields.length > 1 ? (
      <Dropdown
        className="rounded-lg border border-border-primary bg-bg-primary px-2 py-1 text-sm text-text-secondary"
        value={selectedFieldSlug ?? ''}
        onChange={(v) => setManualStartSlug(v || null)}
        options={dateFields.map((f) => ({ value: f.slug, label: f.name }))}
      />
    ) : undefined;

  return (
    <MonthGrid
      year={year}
      month={month}
      events={events}
      noDateEvents={noDateEvents}
      legend={legend}
      onPrevMonth={() => {
        if (month === 0) {
          setYear((y) => y - 1);
          setMonth(11);
        } else setMonth((m) => m - 1);
      }}
      onNextMonth={() => {
        if (month === 11) {
          setYear((y) => y + 1);
          setMonth(0);
        } else setMonth((m) => m + 1);
      }}
      onToday={() => {
        setYear(today.getFullYear());
        setMonth(today.getMonth());
      }}
      onEventClick={handleEventClick}
      onDayClick={onDayClick}
      toolbarExtra={toolbarExtra}
    />
  );
}
